import React from 'react';

import BookOutLinedIcon from '../images/BookOutLined.svg';
import BookMarkBorderOutLinedIcon from '../images/BookMarkBorderOutLined.svg';
import PhoneInTalkOutlinedIcon from '../images/PhoneIn.svg';
import RoundTripConfirmIcon from '../images/RoundTripConfirm.svg';
import RoundTripWaitingIcon from '../images/RoundTripWaiting.svg';

export const tRoweFormatted = <span style={{ whiteSpace: 'nowrap' }}>T. Rowe Price</span>;

export const confirmContentPurple = {
  Confirm: {
      id: 'round-trip-confirm',
      image: RoundTripConfirmIcon,
      cardButtonText: 'Monitor Accounts',
      linkTo: `${document.location.origin}/usis/my-accounts/dashboard`,
      target: '_self',
      header: 'Congratulations! Your changes have been submitted.',
      content: `The processes required to finalize the changes will take a few days. You’ll receive confirmation emails to inform you of your progress. You can always log in to 
      your dashboard to monitor your Retirement Advisory Service accounts.`
  },
  StatusModel: {
      id: 'round-trip-waiting-model',
      image: RoundTripWaitingIcon,
      header: `Congratulations! You've finished your part!`,
      content: `Please encourage your planning partner to confirm the new recommended portfolio.`,
      subText: `This will allow the transition to your new recommended portfolio to begin. Once the transition to your new portfolio begins it will take a few days for all of the 
      necessary processes to complete. You can log in to view your household’s Retirement Advisory Service accounts at any time.`
  },
  StatusAccount: {
      id: 'round-trip-waiting-account',
      image: RoundTripWaitingIcon,
      header: `Congratulations! You've finished your part!`,
      content: `Please encourage your planning partner to confirm the account transfer. Their confirmation is required to add these assets to your 
      Retirement Advisory Service account(s).`,
      subText: `The account(s) that you chose to transfer into your advisory accounts will be transferred in-kind, to keep your assets in the market as long as possible. 
      Once the transfer process begins, it can take approximately <strong>seven to ten business days</strong> for your advisory account(s) to become fully aligned with your 
      recommended portfolio. Please note that during this time there may be a period of up to two days during which the T. Rowe Price account(s) being transferred display a 
      zero balance online, and your advisory accounts may not reflect final balances - this is expected behavior.`
  },
};

export const confirmActionContentPurple = [
  {
    id: 'view-plan',
    image: BookOutLinedIcon,
    buttonText: 'View Plan',
    header: 'View your plan',
    content: `We've designed this plan to help you achieve your financial goals.`
  },
  {
    id: 'call-us',
    image: PhoneInTalkOutlinedIcon,
    linkTo: 'tel:+1-888-358-8490',
    linkText: '1-888-358-8490',
    target: '_self',
    header: 'Questions? Call us.',
    content: 'Talk to a T. Rowe Price associate if you have questions about setting up your new advisory accounts.'
  },
  {
    id: 'view-insights',
    image: BookMarkBorderOutLinedIcon,
    linkTo: 'https://www.troweprice.com/personal-investing/planning-and-research/t-rowe-price-insights/explore-insights.html',
    linkText: 'View Insights',
    header: 'T. Rowe Price Insights',
    trademarked: true,
    content: 'Explore our latest investment thinking, information on retirement planning, and timely perspectives on the markets.'
  }
];


export const panelOneContent ={
    invPropReview: {
        content: `Your Investment Proposal identifies your current accounts that will be used to fund your Retirement Advisory Service accounts and the Recommended Portfolio into
        which they will be implemented. Your Recommended Portfolio represents an asset allocation tailored to your goals, timeline, and risk tolerance.`,
        subContent: `Information about potential tax implications and estimated advisory fees is also included.`
    },
    shortTermDisc: {
        content: `Your advisor will help you identify short-term financial needs. Depending on your situation, these needs may include paying day-to-day expenses in retirement or 
        funding short-term financial goals in pre-retirement years.`,
        subContent: `These are general guidelines to start your conversation:`,
        preRet: `Any goals you plan on achieving in approximately the next 3 years are typically considered short-term goals.`,
        inRet: `We recommend accounting for approximately 13 months of expenses when planning for your short-term financial needs.`,
        disclosure: `For money designated for short-term financial needs, we favor liquidity and avoidance of penalties over other considerations.
        We achieve this by excluding the estimated amount of money you will need during this time frame from your advisory accounts.
        These assets will, however, remain a part of your financial plan.`
    }
};

export const panelTwoContent = {
    accOpen: {
        content: `After reviewing your Investment Proposal, it's time to open your new advisory accounts. You'll need to accept our Terms of Service and answer a few required 
        account opening questions.`,
    },
    accFund: {
        content: `Assets from the accounts you identified to be enrolled and transferred into the Service will be used to fund new advisory accounts. We'll implement your 
        Recommended Portfolio across your household accounts using our implementation strategies.`,
        locStratOne: `We rank the funds in your Recommended Portfolio for tax efficiency then intentionally allocate more tax-efficient funds to your taxable accounts in a way 
        that could help enhance marginal after-tax returns.`,
        locStratTwo: `Each account registration (Roth IRA, Individual, etc.) may have a different asset allocation due to tax efficiency considerations related to asset location
        strategy. The overall portfolio will, however, align with your Recommended Portfolio allocation.`,
        capStrat: `This strategy is generally intended to minimize the tax impact of sale transactions within a client's portfolio by generating losses first and gains last.`,
        extPos: `If your portfolio includes certain funds held at an unrealized gain within a taxable account, also identified as acceptable-to-hold funds, you may 
        choose to maintain these in part or in whole (up to the point of reaching your Recommended Portfolio asset allocation). Future purchases into the applicable asset 
        class will be made in the primary or alternative that you have selected. As re-balancing occurs, the acceptable-to-hold positions may be sold.`
    }
}

export const panelThreeContent = {
    discMngmt: {
        content: (<>When you enroll in the {tRoweFormatted} Retirement Advisory Service, you get a professionally managed portfolio supported by our strategic investing approach.</>),
        subContent: `We put our expertise to work for you. You'll get a professionally managed, broadly diversified portfolio tailored to your goals, timeline, and risk tolerance.
          We'll also continue to consider asset location, capital gain minimization strategies, and tactical and opportunistic portfolio weightings, if applicable, during the ongoing rebalancing of your accounts.`
    },
    advRelationship: {
        content: `Helping people prepare for or navigate through retirement is something our advisors do every day. They are salaried professionals with no sales commissions. 
        Their experience makes them valuable resources.`,
        subContent: `We know you have questions. Your advisor can help you answer them and feel more confident about your financial decisions. Your advisor will be there for you, 
        helping you stay on track towards your goals, even when things change in the market or in your personal life. As an advisory client of T. Rowe Price, you can be confident 
        that your needs will come first.`,
        annCheck: `Your annual check-in is a chance to update your financial plan, check in on your risk tolerance, and update your progress towards
        your financial goals. The annual meeting is a requirement of the Service.`,
        semAnnCheck: `Although not required, the semi-annual check-in is another opportunity to meet with your advisor. This time can be tailored to
        your questions and your needs.`
    }
};

export const balancesModalContent = {
    header: `Important Information Regarding the Updating of Account Balances`,
    content: `T. Rowe Price mutual fund share prices and portfolio unit prices are calculated as of market close each business day and are generally updated on our website each 
    business day between 6:30 p.m. and 8 p.m. ET, at which time current account balances should reflect that updated price information.
    <br /><br /> Prices and balances for positions held in T. Rowe Price brokerage accounts and T. Rowe Price Retirement Advisory Service accounts are generally updated overnight
    between 1 a.m. and 3 a.m. ET, at which time current prices and account balances should reflect information as of the most recent business day's market close.
    <br /><br /> T. Rowe Price ActivePlus Portfolios® account balances are generally updated overnight between 3:30 a.m. and 5:30 a.m. ET, at which time account balances should
    reflect information as of the most recent business day's market close.
    <br /><br /> T. Rowe Price mutual fund transactions entered for your account(s) during the current business day will be processed overnight and should be available to view
    online and reflected in your account balance the following morning. Intraday trading activity and pending account activity in your brokerage account will not be reflected in
    the account balance until the following morning.
    <br /><br /> Balances and holdings displayed for external investment accounts reflect information available on your account sites when last refreshed.`
};

export const cancelModalContent = {
  header: `Are you sure you want to leave?`,
  content: `Leaving this page will clear any information you have entered.`
};

export const contactAdvisorModalContent = {
  header: `Want to transfer additional accounts to Retirement Advisory Service?`,
  content: `Please contact your advisor to guide you through the process of transferring additional accounts to Retirement Advisory Service.`
};

export const footerContent = `<strong>Note:</strong> Please discuss the Investment Proposal with your advisor before proceeding.
<br /><br />Accept our terms of service and answer a few additional questions to initiate your account opening. This will start the transfer for the
exisiting <span style="white-space: nowrap">T. Rowe Price</span> accounts you selected to be enrolled into the Service, if applicable. If you're enrolling
external assets into the Service, you will be prompted to submit external transfers <em>after</em> account opening.
<br /><br />The account opening process will take you about 10 minutes.`;

export const externalAccFooterContent = `Your advisor will work with you to bring the external assets included in your Investment Proposal 
to <span style="white-space: nowrap">T. Rowe Price</span>. After the completion of those transfers, you can begin the process of enrolling in 
the Retirement Advisory Service by opening your new advisory accounts.`

export const footerTitle = {
  regAccContent: `Open your new advisory accounts`,
  extAccContent: `Enrolling in the Retirement Advisory Service`
}

export const footerDisclaimer = {
  averageCostNote: `If you are redeeming covered shares using the Average Cost method, and you proceed with this method,
  you will not be able to revoke Average Cost for these remaining covered shares, and the cost basis of the covered shares
  you currently hold in this fund will be calculated using Average Cost. Noncovered shares will be redeemed before covered
  shares unless you request a redemption using the Specific Lot Identification (SLID) method.`,
  potentialTaxImplications: `Assets from the following accounts could be sold and you could be subject to tax implications
  based on the amount of gains and/or losses. With these potential tax implications, you should consult with a qualified legal
  or tax professional, such as a tax attorney or CPA, regarding your specific situation before making investment decisions.`,
  excessiveTradingPolicy: `As stated in the funds' prospectuses and subject to certain exceptions, each T. Rowe Price fund
  restricts a shareholder's purchases (including through exchanges) into a fund account for a period of 30 calendar days after
  the shareholder has redeemed or exchanged out of that same fund account for any amount greater than $5,000 ("30-Day Purchase Block").
  The calendar day after the date of redemption is considered Day 1 for purposes of computing the period before another purchase may be made.
  Automatic transactions (such as Automatic Asset Builder, automatic exchanges, and automatic withdrawals) generally are exempt from the policy.
  Checkwriting redemptions and transactions solely in money market funds are exempt from this policy.`
};
